/**
 * KioskSettingsSummary Component
 *
 * Compact strip of the most-asked kiosk settings for one system, with a single
 * button into the full Settings page. The full resolved view (and editing)
 * lives on the Settings page; this card only answers "what is this kiosk
 * doing" at a glance.
 */

import React from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Grid } from '@mui/material';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';

import { InfoCard, ReadOnlyField, Button } from '@components/design-system';
import { useEffectiveConfigQuery } from '@features/redux/apis/org';
import { useOrgDetailLink } from './useOrgDetailLink';
import { Selection } from './orgNavTypes';
import { SettingKey, formatSettingValue } from './Panel/systemSettings/settingsMeta';

import type { RootState } from '@features/redux/store';

const SUMMARY_KEYS: SettingKey[] = [
    SettingKey.UiSelection,
    SettingKey.CutPlan,
    SettingKey.WarrantyPlan,
    SettingKey.BladeTracking,
    SettingKey.KitTracking,
];

export type KioskSettingsSummaryProps = {
    stationId: number;
};

export const KioskSettingsSummary: React.FC<KioskSettingsSummaryProps> = ({ stationId }) => {
    const navigate = useNavigate();
    const detailLink = useOrgDetailLink();
    // Effective config is a super-admin endpoint; skip the fetch entirely for everyone else
    const role = useSelector((state: RootState) => state.auth!.Roles.role);
    const { data } = useEffectiveConfigQuery({ stationId }, { skip: !stationId || !role.isSuperAdmin });

    const settingByKey = new Map((data?.settings ?? []).map((setting) => [setting.key as SettingKey, setting]));

    if (!role.isSuperAdmin) return null;

    return (
        <InfoCard
            title='Kiosk Settings'
            icon={<TuneOutlinedIcon />}
            actions={
                <Button variant='secondary' onClick={() => navigate(detailLink(Selection.SystemSettings, stationId))}>
                    Settings
                </Button>
            }>
            <Grid container spacing={2}>
                {SUMMARY_KEYS.filter((key) => settingByKey.has(key)).map((key) => {
                    const setting = settingByKey.get(key)!;
                    return (
                        <ReadOnlyField
                            key={key}
                            label={setting.label}
                            value={formatSettingValue(key, setting.value)}
                            size={4}
                        />
                    );
                })}
            </Grid>
        </InfoCard>
    );
};

export default KioskSettingsSummary;
